import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, addDoc, doc, query, where, getDocs, updateDoc, getDoc, limit } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { FaCheck, FaSpinner, FaExclamationTriangle, FaArrowRight, FaArrowLeft } from 'react-icons/fa';
import { canAddDevice } from '../utils/permissions';

const CreateDevices = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedApi, setSelectedApi] = useState('');
  const [credentials, setCredentials] = useState({
    username: '',
    password: '',
    grant_type: 'password'
  });
  const [accessToken, setAccessToken] = useState('');
  const [tokenData, setTokenData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [connectionData, setConnectionData] = useState(null);
  const [connectionError, setConnectionError] = useState('');
  const [checkingPermissions, setCheckingPermissions] = useState(true);

  useEffect(() => {
    const checkPermissions = async () => {
      const user = auth.currentUser;
      if (!user) {
        navigate('/login');
        return;
      }

      try {
        // First, try to get user document by document ID (matching auth UID)
        let userDoc = await getDoc(doc(db, 'users', user.uid));
        
        // If document doesn't exist with that ID, try querying by uid field
        if (!userDoc.exists()) {
          const usersRef = collection(db, 'users');
          const uidQuery = query(usersRef, where('uid', '==', user.uid), limit(1));
          const querySnapshot = await getDocs(uidQuery);
          
          if (!querySnapshot.empty) {
            // Found user document with uid field matching auth UID
            userDoc = querySnapshot.docs[0];
          }
        }
        
        if (!userDoc.exists()) {
          navigate('/devices');
          return;
        }

        const role = userDoc.data().role;
        if (!canAddDevice(role)) {
          navigate('/devices');
          return;
        }
      } catch (err) {
        console.error('Error checking permissions:', err);
        navigate('/devices');
      } finally {
        setCheckingPermissions(false);
      }
    };

    checkPermissions();
  }, [navigate]);

  if (checkingPermissions) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#00E0E0] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-gray-700">Checking permissions...</h2>
        </div>
      </div>
    );
  }

  const apis = [
    { id: 'andama', name: 'Andama', baseUrl: 'https://api.andama.ai' },
    { id: 'starcom', name: 'Starcom', baseUrl: 'https://mongol.brono.com' },
    { id: 'pgl', name: 'PGL', baseUrl: 'https://api.gpsiot.net' }
  ];

  const buildStarcomUrl = (_baseUrl, username, password) => {
    // Calls are routed through our same-origin serverless proxy (api/starcom.js)
    // to bypass Starcom's lack of CORS headers. The upstream host is hardcoded
    // in the proxy, so `baseUrl` is unused here but kept for future overrides.
    const params = new URLSearchParams({
      commandname: 'get_last_transmits',
      user: username,
      pass: password,
      format: 'json1'
    });
    return `/api/starcom?${params.toString()}`;
  };

  const parseStarcomDateTime = (raw) => {
    if (!raw || typeof raw !== 'string' || raw.length < 14) {
      return new Date().toISOString();
    }
    const y = raw.substring(0, 4);
    const mo = raw.substring(4, 6);
    const d = raw.substring(6, 8);
    const h = raw.substring(8, 10);
    const mi = raw.substring(10, 12);
    const s = raw.substring(12, 14);
    const iso = `${y}-${mo}-${d}T${h}:${mi}:${s}Z`;
    const parsed = new Date(iso);
    return isNaN(parsed.getTime()) ? new Date().toISOString() : parsed.toISOString();
  };

  const toNumberOrZero = (val) => {
    if (val === '' || val === null || val === undefined) return 0;
    const n = Number(val);
    return isNaN(n) ? 0 : n;
  };

  const toNumberOrNull = (val) => {
    if (val === '' || val === null || val === undefined) return null;
    const n = Number(val);
    return isNaN(n) ? null : n;
  };

  const mapStarcomUnitToDeviceData = (unit, connectionId) => {
    const ignitionRaw = unit.ignition;
    const isIgnitionOn = ignitionRaw === '1' || ignitionRaw === 1 || ignitionRaw === true;

    const attributes = JSON.stringify({
      reason: unit.reason ?? null,
      door: unit.door ?? null,
      hood: unit.hood ?? null,
      lock: unit.lock ?? null,
      shock: unit.shock ?? null,
      siren: unit.siren ?? null,
      emergency: unit.emergency ?? null,
      int_lights: unit.int_lights ?? null,
      driver: unit.driver ?? null,
      response: unit.response ?? null,
      logic_state: unit.logic_state ?? null,
      unittype: unit.unittype ?? null,
      unitmodel: unit.unitmodel ?? null,
      software_version: unit.software_version ?? null
    });

    return {
      deviceName: (unit.name || 'Unknown Device').toString().trim(),
      imeiNumber: unit.unitnumber ? String(unit.unitnumber).trim() : null,
      vehicleSpeed: toNumberOrZero(unit.velocity),
      roadSpeed: 0,
      odometer: toNumberOrZero(unit.mileage),
      isIgnitionOn,
      heading: toNumberOrZero(unit.heading),
      lat: unit.latitude !== undefined && unit.latitude !== null ? String(unit.latitude) : '0',
      lon: unit.longitude !== undefined && unit.longitude !== null ? String(unit.longitude) : '0',
      location: 'Unknown',
      eventName: 'Position Report',
      gpsDateTime: parseStarcomDateTime(unit.datetime_actual || unit.datetime_utc),
      battery: toNumberOrZero(unit.battery),
      altitude: unit.altitude !== undefined && unit.altitude !== null ? String(unit.altitude) : '0',
      sensorData: '{}',
      workingHours: 0,
      attributes,
      fuel: toNumberOrNull(unit.fuel),
      mainVoltage: toNumberOrZero(unit.main_voltage),
      apiProvider: 'starcom',
      apiConnectionId: connectionId,
      status: 'active',
      lastUpdated: new Date().toISOString()
    };
  };

  const handleApiSelect = (apiId) => {
    setSelectedApi(apiId);
    setError('');
  };

  const handleCredentialChange = (e) => {
    const { name, value } = e.target;
    setCredentials(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const handleGetToken = async () => {
    if (!selectedApi) {
      setError('Please select an API provider');
      return;
    }

    if (!credentials.username || !credentials.password) {
      setError('Please enter both username and password');
      return;
    }

    setLoading(true);
    setError('');
    setTokenData(null);

    try {
      const api = apis.find(a => a.id === selectedApi);

      // Starcom has no OAuth step - fetch devices directly
      if (selectedApi === 'starcom') {
        const starcomUrl = buildStarcomUrl(api.baseUrl, credentials.username, credentials.password);
        const response = await fetch(starcomUrl, {
          method: 'GET',
          cache: 'no-store'
        });

        if (!response.ok) {
          setError(`Failed to fetch from Starcom: ${response.status}`);
          setTokenData(null);
          return;
        }

        const data = await response.json();
        if (!Array.isArray(data)) {
          setError('Unexpected Starcom response format (expected array)');
          setTokenData(null);
          return;
        }

        setAccessToken('');
        setTokenData({ token_type: 'none', expires_in: 0 });
        setConnectionData({ Data: data });
        setError('');
        setCurrentStep(3);
        return;
      }

      const tokenUrl = `${api.baseUrl}/token`;

      // Create form data for x-www-form-urlencoded
      const formData = new URLSearchParams();
      formData.append('username', credentials.username);
      formData.append('password', credentials.password);
      formData.append('grant_type', credentials.grant_type);

      const response = await fetch(tokenUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData.toString()
      });

      const data = await response.json();

      if (response.ok && data.access_token) {
        setAccessToken(data.access_token);
        setTokenData(data);
        setError('');
        // Auto-advance to step 3
        setCurrentStep(3);
      } else {
        setError(data.error_description || data.error || 'Failed to get access token');
        setTokenData(null);
      }
    } catch (err) {
      setError(`Connection error: ${err.message}`);
      setTokenData(null);
    } finally {
      setLoading(false);
    }
  };

  const handleTestConnection = async () => {
    setLoading(true);
    setConnectionError('');
    setConnectionData(null);

    try {
      const api = apis.find(a => a.id === selectedApi);

      // Starcom: re-fetch the same endpoint as a "connection test"
      if (selectedApi === 'starcom') {
        if (!credentials.username || !credentials.password) {
          setConnectionError('Missing Starcom credentials');
          return;
        }
        const starcomUrl = buildStarcomUrl(api.baseUrl, credentials.username, credentials.password);
        const response = await fetch(starcomUrl, {
          method: 'GET',
          cache: 'no-store'
        });

        if (!response.ok) {
          setConnectionError(`Failed to fetch from Starcom: ${response.status}`);
          setConnectionData(null);
          return;
        }

        const data = await response.json();
        if (!Array.isArray(data)) {
          setConnectionError('Unexpected Starcom response format (expected array)');
          setConnectionData(null);
          return;
        }

        setConnectionData({ Data: data });
        setConnectionError('');
        return;
      }

      if (!accessToken) {
        setConnectionError('No access token available');
        return;
      }

      const testUrl = `${api.baseUrl}/api/Asset/GetResellerDevicesCurrentData`;

      const response = await fetch(testUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        }
      });

      const data = await response.json();

      if (response.ok) {
        setConnectionData(data);
        setConnectionError('');
      } else {
        setConnectionError(data.Message || data.error || 'Failed to fetch device data');
        setConnectionData(null);
      }
    } catch (err) {
      setConnectionError(`Connection error: ${err.message}`);
      setConnectionData(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveDevices = async () => {
    if (!connectionData || !connectionData.Data || connectionData.Data.length === 0) {
      setError('No device data to save');
      return;
    }

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const api = apis.find(a => a.id === selectedApi);

      // Save API connection info (including password and token for auto-connection)
      const connectionRef = collection(db, 'api_connections');
      const baseConnectionPayload = {
        apiProvider: selectedApi,
        apiName: api.name,
        baseUrl: api.baseUrl,
        username: credentials.username,
        password: credentials.password,
        createdAt: new Date().toISOString(),
        lastSynced: new Date().toISOString(),
        lastSyncStatus: 'success',
        autoConnect: true
      };

      // Starcom has no token; PGL/Andama persist token + expiration details
      const connectionPayload = selectedApi === 'starcom'
        ? baseConnectionPayload
        : {
            ...baseConnectionPayload,
            accessToken: accessToken,
            tokenType: tokenData.token_type,
            expiresIn: tokenData.expires_in,
            expiration: tokenData.expiration
          };

      const connectionDoc = await addDoc(connectionRef, connectionPayload);
      const connectionId = connectionDoc.id;

      // Save each device
      const devicesRef = collection(db, 'devices');
      let savedCount = 0;
      let updatedCount = 0;

      for (const deviceData of connectionData.Data) {
        try {
          const apiProvider = selectedApi;
          let normalizedImei;
          let deviceName;

          if (selectedApi === 'starcom') {
            normalizedImei = deviceData.unitnumber ? String(deviceData.unitnumber).trim() : null;
            deviceName = (deviceData.name || 'Unknown Device').toString().trim();
          } else {
            normalizedImei = deviceData.ImeiNumber
              ? String(deviceData.ImeiNumber).trim()
              : null;
            deviceName = (deviceData.DeviceName || 'Unknown Device').trim();
          }

          // Check if device already exists - try IMEI first, then fallback to name + apiProvider
          let existingDoc = null;

          if (normalizedImei) {
            // First, try to find by IMEI (normalized to string)
            const imeiQuery = query(
              collection(db, 'devices'),
              where('imeiNumber', '==', normalizedImei)
            );
            const imeiSnapshot = await getDocs(imeiQuery);

            if (!imeiSnapshot.empty) {
              existingDoc = imeiSnapshot.docs[0];
            }
          }

          // If not found by IMEI, check by device name + apiProvider combination
          // This prevents duplicates when IMEI is missing or different
          if (!existingDoc && deviceName !== 'Unknown Device') {
            const nameQuery = query(
              collection(db, 'devices'),
              where('deviceName', '==', deviceName),
              where('apiProvider', '==', apiProvider)
            );
            const nameSnapshot = await getDocs(nameQuery);

            if (!nameSnapshot.empty) {
              existingDoc = nameSnapshot.docs[0];
            }
          }

          // Prepare device data, preserving existing IMEI if new one is missing
          const existingData = existingDoc ? existingDoc.data() : null;
          const finalImei = normalizedImei || existingData?.imeiNumber || null;

          let deviceDoc;
          if (selectedApi === 'starcom') {
            const mapped = mapStarcomUnitToDeviceData(deviceData, connectionId);
            deviceDoc = {
              ...mapped,
              imeiNumber: finalImei,
              createdAt: new Date().toISOString()
            };
          } else {
            deviceDoc = {
              deviceName: deviceName,
              imeiNumber: finalImei,
              vehicleSpeed: deviceData.VehicleSpeed || 0,
              roadSpeed: deviceData.RoadSpeed || 0,
              odometer: deviceData.Odometer || 0,
              isIgnitionOn: deviceData.IsIgnitionOn === 'True' || deviceData.IsIgnitionOn === true,
              heading: deviceData.Heading || 0,
              lat: deviceData.Lat || '0',
              lon: deviceData.Lon || '0',
              location: deviceData.Location || 'Unknown',
              eventName: deviceData.EventName || 'Position Report',
              gpsDateTime: deviceData.GPSDateTime || new Date().toISOString(),
              battery: deviceData.Battery || 0,
              altitude: deviceData.Altitude || '0',
              sensorData: deviceData.SensorData || '{}',
              workingHours: deviceData.WorkingHours || 0,
              attributes: deviceData.Attributes || '{}',
              apiProvider: apiProvider,
              apiConnectionId: connectionId,
              status: 'active',
              lastUpdated: new Date().toISOString(),
              createdAt: new Date().toISOString()
            };
          }

          if (!existingDoc) {
            // Create new device
            await addDoc(devicesRef, deviceDoc);
            savedCount++;
          } else {
            // Update existing device - preserve original creation date and vehicleId if exists
            const updateData = {
              ...deviceDoc,
              createdAt: existingData.createdAt // Preserve original creation date
            };
            
            // Preserve vehicleId if it exists (don't overwrite device-vehicle link)
            if (existingData.vehicleId) {
              updateData.vehicleId = existingData.vehicleId;
            }
            
            await updateDoc(doc(db, 'devices', existingDoc.id), updateData);
            updatedCount++;
          }
        } catch (deviceError) {
          const identifier = deviceData.ImeiNumber || deviceData.DeviceName || deviceData.unitnumber || deviceData.name;
          console.error(`Error saving device ${identifier}:`, deviceError);
        }
      }

      setSuccess(`Successfully saved ${savedCount} new devices and updated ${updatedCount} existing devices!`);
      
      // Navigate to devices page after a short delay
      setTimeout(() => {
        navigate('/devices');
      }, 2000);
    } catch (err) {
      console.error('Error saving devices:', err);
      setError(`Failed to save devices: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleNext = () => {
    if (currentStep === 1 && selectedApi) {
      setCurrentStep(2);
    } else if (currentStep === 2) {
      handleGetToken();
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleReset = () => {
    setCurrentStep(1);
    setSelectedApi('');
    setCredentials({
      username: '',
      password: '',
      grant_type: 'password'
    });
    setAccessToken('');
    setTokenData(null);
    setConnectionData(null);
    setError('');
    setConnectionError('');
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Create Device Connection</h1>
        <p className="text-gray-600 mt-2">Connect to a GPS tracking API to import devices</p>
      </div>

      {/* Progress Steps */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          {[1, 2, 3].map((step) => (
            <React.Fragment key={step}>
              <div className="flex items-center">
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${
                    currentStep >= step
                      ? 'bg-[#00E0E0] text-white'
                      : 'bg-gray-200 text-gray-600'
                  }`}
                >
                  {currentStep > step ? <FaCheck /> : step}
                </div>
                <div className="ml-2 hidden sm:block">
                  <div className={`text-sm font-medium ${
                    currentStep >= step ? 'text-[#00E0E0]' : 'text-gray-500'
                  }`}>
                    {step === 1 && 'Select API'}
                    {step === 2 && 'Credentials'}
                    {step === 3 && 'Confirm'}
                  </div>
                </div>
              </div>
              {step < 3 && (
                <div
                  className={`flex-1 h-1 mx-4 ${
                    currentStep > step ? 'bg-[#00E0E0]' : 'bg-gray-200'
                  }`}
                />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Step 1: Select API */}
      {currentStep === 1 && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Step 1: Select API Provider</h2>
          <p className="text-gray-600 mb-6">Choose the GPS tracking API you want to connect to</p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {apis.map((api) => (
              <button
                key={api.id}
                onClick={() => handleApiSelect(api.id)}
                className={`p-6 border-2 rounded-lg text-left transition-all ${
                  selectedApi === api.id
                    ? 'border-[#00E0E0] bg-[#00E0E0]/10'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-lg font-semibold text-gray-800">{api.name}</h3>
                  {selectedApi === api.id && (
                    <FaCheck className="text-[#00E0E0]" />
                  )}
                </div>
                <p className="text-sm text-gray-600">{api.baseUrl}</p>
              </button>
            ))}
          </div>

          {error && (
            <div className="mt-4 p-3 bg-red-100 text-red-700 rounded-lg flex items-center gap-2">
              <FaExclamationTriangle />
              {error}
            </div>
          )}

          <div className="mt-6 flex justify-end">
            <button
              onClick={handleNext}
              disabled={!selectedApi}
              className={`px-6 py-2 rounded-lg flex items-center gap-2 ${
                selectedApi
                  ? 'bg-[#00E0E0] text-white hover:bg-[#00B8B8]'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              } transition-colors`}
            >
              Next <FaArrowRight />
            </button>
          </div>
        </div>
      )}

      {/* Step 2: Enter Credentials */}
      {currentStep === 2 && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Step 2: Enter API Credentials</h2>
          <p className="text-gray-600 mb-6">
            Enter your API credentials for {apis.find(a => a.id === selectedApi)?.name}
          </p>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {selectedApi === 'starcom' ? 'Username' : 'Username (Web API Key)'}
              </label>
              <input
                type="text"
                name="username"
                value={credentials.username}
                onChange={handleCredentialChange}
                placeholder={selectedApi === 'starcom' ? 'Enter your Starcom username' : 'Enter your Web API Key'}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#00E0E0] focus:border-[#00E0E0]"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {selectedApi === 'starcom' ? 'Password' : 'Password (Web Secret Key)'}
              </label>
              <input
                type="password"
                name="password"
                value={credentials.password}
                onChange={handleCredentialChange}
                placeholder={selectedApi === 'starcom' ? 'Enter your Starcom password' : 'Enter your Web Secret Key'}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#00E0E0] focus:border-[#00E0E0]"
              />
            </div>

            {selectedApi !== 'starcom' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Grant Type
                </label>
                <input
                  type="text"
                  name="grant_type"
                  value={credentials.grant_type}
                  onChange={handleCredentialChange}
                  className="w-full px-4 py-2 border rounded-lg bg-gray-50 focus:ring-2 focus:ring-[#00E0E0] focus:border-[#00E0E0]"
                  readOnly
                />
                <p className="text-xs text-gray-500 mt-1">Default: password</p>
              </div>
            )}
          </div>

          {error && (
            <div className="mt-4 p-3 bg-red-100 text-red-700 rounded-lg flex items-center gap-2">
              <FaExclamationTriangle />
              {error}
            </div>
          )}

          {tokenData && selectedApi !== 'starcom' && (
            <div className="mt-4 p-3 bg-green-100 text-green-700 rounded-lg">
              <p className="font-semibold">Token obtained successfully!</p>
              <p className="text-sm mt-1">Expires in: {tokenData.expires_in} seconds</p>
            </div>
          )}

          <div className="mt-6 flex justify-between">
            <button
              onClick={handleBack}
              className="px-6 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 flex items-center gap-2 transition-colors"
            >
              <FaArrowLeft /> Back
            </button>
            <button
              onClick={handleGetToken}
              disabled={loading || !credentials.username || !credentials.password}
              className={`px-6 py-2 rounded-lg flex items-center gap-2 ${
                loading || !credentials.username || !credentials.password
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-[#00E0E0] text-white hover:bg-[#00B8B8]'
              } transition-colors`}
            >
              {loading ? (
                <>
                  <FaSpinner className="animate-spin" /> Connecting...
                </>
              ) : (
                <>
                  {selectedApi === 'starcom' ? 'Fetch Devices' : 'Get Token'} <FaArrowRight />
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Step 3: Confirm Connection */}
      {currentStep === 3 && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Step 3: Confirm Connection</h2>
          <p className="text-gray-600 mb-6">
            Test the connection and verify device data retrieval
          </p>

          {tokenData && selectedApi !== 'starcom' && (
            <div className="mb-6 p-4 bg-gray-50 rounded-lg">
              <h3 className="font-semibold text-gray-800 mb-2">Token Information</h3>
              <div className="space-y-1 text-sm">
                <p><span className="font-medium">Token Type:</span> {tokenData.token_type}</p>
                <p><span className="font-medium">Expires In:</span> {tokenData.expires_in} seconds</p>
                {tokenData.expiration && (
                  <p><span className="font-medium">Expiration:</span> {tokenData.expiration}</p>
                )}
              </div>
            </div>
          )}

          <button
            onClick={handleTestConnection}
            disabled={loading || (selectedApi !== 'starcom' && !accessToken)}
            className={`w-full px-6 py-3 rounded-lg flex items-center justify-center gap-2 mb-4 ${
              loading || (selectedApi !== 'starcom' && !accessToken)
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-[#00E0E0] text-white hover:bg-[#00B8B8]'
            } transition-colors`}
          >
            {loading ? (
              <>
                <FaSpinner className="animate-spin" /> Testing Connection...
              </>
            ) : (
              <>
                Test Connection
              </>
            )}
          </button>

          {error && (
            <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-lg flex items-center gap-2">
              <FaExclamationTriangle />
              {error}
            </div>
          )}

          {success && (
            <div className="mb-4 p-3 bg-green-100 text-green-700 rounded-lg flex items-center gap-2">
              <FaCheck />
              {success}
            </div>
          )}

          {connectionError && (
            <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-lg flex items-center gap-2">
              <FaExclamationTriangle />
              {connectionError}
            </div>
          )}

          {connectionData && (
            <div className="mb-4">
              <div className="p-3 bg-green-100 text-green-700 rounded-lg flex items-center gap-2 mb-4">
                <FaCheck />
                Connection successful! Device data retrieved.
              </div>
              
              <div className="border rounded-lg overflow-hidden">
                <div className="bg-gray-100 px-4 py-2 border-b">
                  <h3 className="font-semibold text-gray-800">API Response Data</h3>
                </div>
                <div className="p-4 bg-gray-50 overflow-x-auto">
                  <pre className="text-xs text-gray-700 whitespace-pre-wrap">
                    {JSON.stringify(connectionData, null, 2)}
                  </pre>
                </div>
              </div>
            </div>
          )}

          <div className="mt-6 flex justify-between">
            <button
              onClick={handleBack}
              className="px-6 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 flex items-center gap-2 transition-colors"
            >
              <FaArrowLeft /> Back
            </button>
            <div className="flex gap-2">
              <button
                onClick={handleReset}
                className="px-6 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Start Over
              </button>
              {connectionData && (
                <button
                  onClick={handleSaveDevices}
                  disabled={saving}
                  className={`px-6 py-2 rounded-lg flex items-center gap-2 ${
                    saving
                      ? 'bg-gray-400 text-white cursor-not-allowed'
                      : 'bg-green-600 text-white hover:bg-green-700'
                  } transition-colors`}
                >
                  {saving ? (
                    <>
                      <FaSpinner className="animate-spin" /> Saving...
                    </>
                  ) : (
                    'Save Devices'
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreateDevices;

